import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import { API_DATE_FORMAT } from "@/src/constants/dateFormat";

import {
  bucketTimeline,
  fillMissingDates,
  getChartCardStatus,
  getTimelineGranularity,
  hasSeriesData,
  keepWithinRange,
} from "./charts.utils";

import type { Moment } from "moment";

import type { Query } from "@/src/api/BaseAPI";

import type {
  ChartCardStatus,
  TimelineDatum,
  TimelineGranularity,
  TimelineKind,
} from "./charts.types";

interface UseTimelineChartParams<TResponse, TKey extends string> {
  query: Query<TResponse>;
  buildTimeline: (response: TResponse | undefined) => TimelineDatum<TKey>[];
  keyList: readonly TKey[];
  startDate: Moment;
  endDate: Moment;
  kind: TimelineKind;
}

interface UseTimelineChartReturn<TKey extends string> {
  timelineList: TimelineDatum<TKey>[];
  granularity: TimelineGranularity;
  status: ChartCardStatus;
  isFetching: boolean;
}

/** `buildTimeline` must keep a stable identity (module-level function) to preserve the memo. */
function useTimelineChart<TResponse, TKey extends string>({
  query,
  buildTimeline,
  keyList,
  startDate,
  endDate,
  kind,
}: UseTimelineChartParams<TResponse, TKey>): UseTimelineChartReturn<TKey> {
  const { data, isLoading, isError, isFetching, isPreviousData } =
    useQuery(query);

  const { timelineList, granularity, isEmpty } = useMemo(() => {
    const startDay = startDate.format(API_DATE_FORMAT);
    const endDay = endDate.format(API_DATE_FORMAT);
    // A missing day is unknown for a stock, not zero.
    const displayedList =
      kind === "stock"
        ? keepWithinRange(buildTimeline(data), startDay, endDay)
        : fillMissingDates(buildTimeline(data), keyList, startDay, endDay);
    // The displayed range (after fillMissingDates trimmed huge ranges) drives the granularity.
    const displayedGranularity = getTimelineGranularity(
      displayedList[0]?.date ?? startDay,
      displayedList.at(-1)?.date ?? endDay,
    );
    return {
      timelineList: bucketTimeline(
        displayedList,
        keyList,
        displayedGranularity,
        kind === "stock" ? "mean" : "sum",
      ),
      granularity: displayedGranularity,
      isEmpty: !hasSeriesData(displayedList, keyList),
    };
  }, [buildTimeline, data, keyList, startDate, endDate, kind]);

  // Previous-period data over the new range is all zeros: wait, don't claim "no data".
  const status = getChartCardStatus({
    isLoading: isLoading || (isPreviousData && isEmpty),
    isError,
    isEmpty,
  });

  return { timelineList, granularity, status, isFetching };
}

export default useTimelineChart;
